import React, { useEffect, useState, useRef, useCallback } from "react";
import { minMaxByType, pixelByMilliVoltage, isDetectingStringByType } from "../plugins/common";
import { playerBedDataState, lastRespValueStateFamily, globalParameterColorState } from "../plugins/recoil";
import { useRecoilState } from "recoil";
import { useBedData } from '../context/BedDataContext';

const Svg = ({
    intervalIndex,
    isFirst,
    isPlayer,
    isPlaying,
    curSec,
    type,
    data,
    width,
    height,
    stroke = "#00f",
    strokeWidth = 1,
    yOffset = 0,
    textYOffset = 21,
    count,
    bedInfo,
    bufferCount,
    bufferMaxCount,
    viewType,
    dataCountPerFrame,
    handleBufferCount,
    isDetecting,
    isShowECGPacer,
    componentId,
    ...props
}) => {
    const serialKey = isPlayer ? `player_${bedInfo.serial}` : bedInfo.serial;
    const [middleVal, setMiddleVal] = useState(0)
    const [points, setPoints] = useState([]);
    const [maxData, setMaxData] = useState(0);
    const [minData, setMinData] = useState(0);
    const [isInit, setIsInit] = useState(false)
    const [dataCount, setDataCount] = useState(0)
    const [displaySeconds, setDisplaySeconds] = useState(0)
    const [hrData, setHrData] = useState(new Array(count).fill(null))
    const [hrDataQueue, setHrDataQueue] = useState([])
    const {
      consumeWaveData, getDataQueue, pushToDataQueue, sliceDataQueue,
      trimDataQueue, clearDataQueue, getDataQueueLength,
      getChartIndex, setChartIndex: setChartIndexCtx,
      getChartInit, setChartInit, subscribeChartInit
    } = useBedData();
    
    const [chartInitState, setChartInitStateLocal] = useState(() => getChartInit(serialKey));
    const [chartIndex, setChartIndexLocal] = useState(() => getChartIndex(serialKey));
    
    // 🔥 dataQueue를 ref로 관리 (Recoil 대신)
    const dataQueueLengthRef = useRef(0);
    const [dataQueueLength, setDataQueueLength] = useState(0);
    
    const [playerBedData, setPlayerBedData] = useRecoilState(playerBedDataState)
    const [isComponentReady, setIsComponentReady] = useState(false);
    const [parameterColors, ] = useRecoilState(globalParameterColorState);
    const [lastRespValue, setLastRespValue] = useRecoilState(
        lastRespValueStateFamily({ serial: serialKey, type })
    );

    const dataCountRef = useRef(0);
    const hrDataRef = useRef(new Array(count).fill(null));
    const isRenderingRef = useRef(false);
    const globalDataIndexRef = useRef(0);
    const queueDataIndexRef = useRef(0);
    const hrDataQueueRef = useRef(isPlayer ? hrDataQueue : []);
    
    // 🔥 매 렌더마다 최신 큐 상태를 ref에 업데이트
    if (isPlayer) {
      hrDataQueueRef.current = hrDataQueue;
    } else {
      hrDataQueueRef.current = getDataQueue(serialKey, type).data;
    }

    const performanceRef = useRef({ 
        lastFrameTime: 0, 
        avgFrameTime: 16.67,
        consecutiveSlowFrames: 0,
        accumulatedTime: 0 
    });
    
    // 🔥 setChartInitState를 Context로 위임하는 래퍼 함수
    const setChartInitState = useCallback((updater) => {
        setChartInit(serialKey, updater);
        // 로컬 state도 업데이트 (리렌더용)
        setChartInitStateLocal(getChartInit(serialKey));
    }, [serialKey, setChartInit, getChartInit]);

    // 🔥 setChartIndex를 Context로 위임하는 래퍼 — animate 내부에서 호출
    const setChartIndex = useCallback((value) => {
        setChartIndexCtx(serialKey, value);
        // ❌ setChartIndexLocal 호출 안 함 — animate에서 초당 60회 호출되므로 리렌더 불필요
    }, [serialKey, setChartIndexCtx]);

    const getMinMaxWithType = () => {
        if (minMaxByType[type]) return minMaxByType[type]
        return { max: 50, min: -50 }
    }

    // 🔥 chartInit 구독 — 다른 SvgChart가 setChartInit 호출 시 알림 받음
    useEffect(() => {
        const unsubscribe = subscribeChartInit(serialKey, (newState) => {
            setChartInitStateLocal(newState);
        });
        return unsubscribe;
    }, [serialKey]);

    // 컴포넌트 등록 및 준비 상태 관리
    useEffect(() => {
        if (!componentId || isPlayer) return;

        setChartInitState(prev => ({
            ...prev,
            totalComponents: prev.totalComponents + 1
        }));

        return () => {
            setChartInitState(prev => {
                const newReadyComponents = new Set([...prev.readyComponents].filter(id => id !== componentId));
                const newTotalComponents = Math.max(0, prev.totalComponents - 1);
                
                if (newTotalComponents === 0) {
                    return {
                        isGlobalInit: false,
                        initStartTime: null,
                        readyComponents: new Set(),
                        totalComponents: 0
                    };
                }
                
                return {
                    ...prev,
                    readyComponents: newReadyComponents,
                    totalComponents: newTotalComponents
                };
            });
            if (!isPlayer) {
                clearDataQueue(serialKey, type);
            }
        };
    }, [componentId, isPlayer, bedInfo.serial]);

    // 개별 컴포넌트의 준비 상태 확인
    useEffect(() => {
        if (isPlayer) return;
        
        if (chartInitState.isGlobalInit) {
            setIsComponentReady(true)
            setChartInitState(prev => ({
                ...prev,
                readyComponents: new Set([...prev.readyComponents, componentId])
            }));
        } else {
            const queueLen = getDataQueueLength(serialKey, type);
            const hasEnoughData = queueLen >= bufferCount;
            if (hasEnoughData && !isComponentReady) {
                setIsComponentReady(true);
                setChartInitState(prev => ({
                    ...prev,
                    readyComponents: new Set([...prev.readyComponents, componentId])
                }));
            }
        }
    }, [dataQueueLength, bufferCount, isComponentReady, componentId, isPlayer, chartInitState.isGlobalInit]);

    // 전체 초기화 조건 확인
    useEffect(() => {
        if (isPlayer) return;
        
        const { readyComponents, totalComponents, isGlobalInit } = chartInitState;
        
        if (readyComponents.size === totalComponents && 
            totalComponents > 0 && 
            !isGlobalInit) {
            
            setChartInitState(prev => ({
                ...prev,
                isGlobalInit: true,
                initStartTime: Date.now()
            }));
            return;
        }
        
        const timeoutId = setTimeout(() => {
            if (!isGlobalInit && readyComponents.size > 0) {
                console.warn('일부 컴포넌트가 준비되지 않았지만 타임아웃으로 인해 강제 초기화합니다.');
                setChartInitState(prev => ({
                    ...prev,
                    isGlobalInit: true,
                    initStartTime: Date.now()
                }));
            }
        }, 1500);

        if (isGlobalInit) {
            clearTimeout(timeoutId);
        }

        return () => clearTimeout(timeoutId);
    }, [chartInitState, isPlayer]);

    // isInit 설정
    useEffect(() => {
        if (isPlayer) {
            if (hrDataQueue.length > 0 && !isInit) {
                setIsInit(true);
            }
        } else {
            if (chartInitState.isGlobalInit && isComponentReady && !isInit) {
                // 🔥 chartIndex를 ref에서 읽기
                const currentChartIndex = getChartIndex(serialKey);
                globalDataIndexRef.current = currentChartIndex;
                queueDataIndexRef.current = 0;
                
                const queue = getDataQueue(serialKey, type);
                const totalDataInQueue = queue.data.reduce((sum, arr) => sum + (arr?.length || 0), 0);
                
                if (currentChartIndex > totalDataInQueue) {
                    globalDataIndexRef.current = currentChartIndex;
                    queueDataIndexRef.current = 0;
                } else if (currentChartIndex > 0) {
                    let remainingSkip = currentChartIndex;
                    let queueIdx = 0;
                    
                    while (remainingSkip > 0 && queueIdx < queue.data.length) {
                        const currentQueueLength = queue.data[queueIdx]?.length || 0;
                        
                        if (remainingSkip >= currentQueueLength) {
                            remainingSkip -= currentQueueLength;
                            queueIdx++;
                        } else {
                            queueDataIndexRef.current = remainingSkip;
                            break;
                        }
                    }
                }
                
                setIsInit(true);
            }
        }
    }, [chartInitState.isGlobalInit, isComponentReady, isPlayer, hrDataQueue.length, dataQueueLength, isInit]);

    useEffect(() => {
        if (isPlayer) return
        
        const newHrData = new Array(count).fill(null);
        setHrData(newHrData);
        hrDataRef.current = newHrData;
        
        const queue = getDataQueue(serialKey, type);
        if (queue.data.length > 0) {
            sliceDataQueue(serialKey, type, 1);
            hrDataQueueRef.current = getDataQueue(serialKey, type).data;
            
            globalDataIndexRef.current = 0;
            queueDataIndexRef.current = 0;
        } else {
            globalDataIndexRef.current = 0;
            queueDataIndexRef.current = 0;
            hrDataQueueRef.current = [];
        }
    }, []);

    // type, count 변경 시 초기화
    useEffect(() => {
        setDisplaySeconds((type === 'RESP' || type === 'PLETH') ? count / 125 : count / 250);
        setIsInit(false);
        setIsComponentReady(false);
        
        if (!isPlayer && componentId) {
            setChartInitState(prev => ({
                ...prev,
                readyComponents: new Set([...prev.readyComponents].filter(id => id !== componentId))
            }));
        }

        setHrDataQueue([]);
        setHrData(new Array(count).fill(null));
        hrDataRef.current = new Array(count).fill(null);
        
        // 🔥 ref에서 읽기
        const baseIndex = isPlayer ? 0 : getChartIndex(serialKey);
        globalDataIndexRef.current = baseIndex;
        queueDataIndexRef.current = 0;
        dataCountRef.current = 0;

        if (!isPlayer) {
            clearDataQueue(serialKey, type);
        }
    }, [type, count, isPlayer, componentId]);

    useEffect(() => {
        if (isComponentReady) {
            setHrDataQueue([]);
            setHrData(new Array(count).fill(null));
            hrDataRef.current = new Array(count).fill(null);
            queueDataIndexRef.current = 0;
            dataCountRef.current = 0;
        }
    }, [isComponentReady])
        

    useEffect(() => {
        const minMax = getMinMaxWithType();
        const numericHrData = hrData.filter(item => typeof item === 'number');
    
        const max = Math.max(...numericHrData, minMax.max);
        const min = Math.min(...numericHrData, minMax.min);
        setMiddleVal((max + min) / 2);
        setMaxData(max);
        setMinData(min);
    
        const midValue = (max + min) / 2;
        const widthValue = width || 0;
        const heightValue = height || 0;
        const halfHeight = heightValue / 2;
    
        const pathParts = [];
        let lastPointWasNumber = false;

        for (let idx = 0; idx < hrData.length; idx++) {
            const val = hrData[idx];

            if (typeof val === 'number') {
                const x = (idx / (count - 1)) * widthValue;
    
                const denominator = max - min;
                let y;
                if (denominator === 0) {
                    y = halfHeight + yOffset;
                } else {
                    y = halfHeight - ((val - midValue) / denominator) * halfHeight + yOffset;
                }

                if (!lastPointWasNumber) {
                    pathParts.push(`M ${x} ${y}`);
                } else {
                    pathParts.push(`L ${x} ${y}`);
                }
                lastPointWasNumber = true;
            } else {
                lastPointWasNumber = false;
            }
        }
    
        setPoints(pathParts.join(" "));
    }, [hrData, width, height, yOffset, bedInfo]);

    // 플레이어 리셋 시
    useEffect(() => {
        if (isPlayer && !isPlaying && curSec === 0) {
            dataCountRef.current = 0;
            globalDataIndexRef.current = 0;
            queueDataIndexRef.current = 0;
            setHrDataQueue([]);
            // 🔥 플레이어도 ref 기반으로 할 수 있지만, 플레이어는 빈도가 낮으므로 유지
            clearDataQueue(serialKey, type);
            setIsInit(false);
            setHrData(new Array(count).fill(null));
            hrDataRef.current = new Array(count).fill(null);
        }
    }, [isPlayer, isPlaying, curSec, count]);

    // 그래프 렌더링
    useEffect(() => {
        if (!isInit) return;
        
        let animationFrameId;

        const animate = (currentTime) => {
            if (isPlayer && !isPlaying) {
                animationFrameId = requestAnimationFrame(animate);
                return;
            }

            if (performanceRef.current.lastFrameTime > 0) {
                const frameTime = currentTime - performanceRef.current.lastFrameTime;
            }

            if (isRenderingRef.current) {
                animationFrameId = requestAnimationFrame(animate);
                return;
            }
            
            isRenderingRef.current = true;
            
            try {
                const frameTime = currentTime - performanceRef.current.lastFrameTime;
                
                if (performanceRef.current.lastFrameTime > 0) {
                    performanceRef.current.avgFrameTime = 
                        performanceRef.current.avgFrameTime * 0.9 + frameTime * 0.1;
                    
                    performanceRef.current.accumulatedTime += frameTime;
                }
                
                setChartIndex(globalDataIndexRef.current);

                const dataPerSecond = (type === 'RESP' || type === 'PLETH') ? 125 : 250;
                const msPerData = 1000 / dataPerSecond;
                const dataToProcess = Math.floor(performanceRef.current.accumulatedTime / msPerData);
                
                if (dataToProcess > 0) {
                    performanceRef.current.accumulatedTime -= dataToProcess * msPerData;
                    
                    const updates = {
                        newHrData: [...hrDataRef.current],
                        newQueue: [...hrDataQueueRef.current],
                        queueChanged: false,
                        originalQueueLength: hrDataQueueRef.current.length
                    };
                    
                    let processedDataCount = 0;
                    const prevGlobalDataIndex = globalDataIndexRef.current;

                    while (processedDataCount < dataToProcess) {
                        const isRespOrPleth = type === 'RESP' || type === 'PLETH';

                        if (isRespOrPleth && globalDataIndexRef.current % 2 !== 0) {
                            globalDataIndexRef.current++;
                            continue;
                        }

                        const dataIndexToProcess = isRespOrPleth ? Math.floor(globalDataIndexRef.current / 2) : globalDataIndexRef.current;
                        const targetIndex = dataIndexToProcess % count;

                        globalDataIndexRef.current++;

                        let hrValue = null;

                        if (updates.newQueue.length > 0 && updates.newQueue[0]) {
                            let firstQueue = updates.newQueue[0];

                            while (firstQueue && (!Array.isArray(firstQueue) || firstQueue.length === 0)) {
                                updates.newQueue.shift();
                                updates.queueChanged = true;
                                queueDataIndexRef.current = 0;
                                firstQueue = updates.newQueue[0];
                            }

                            if (firstQueue) {
                                const dataIndex = queueDataIndexRef.current;
                                hrValue = isDetecting ? firstQueue[dataIndex] : null;
                                
                                queueDataIndexRef.current++;
                                
                                if (queueDataIndexRef.current >= firstQueue.length) {
                                    updates.newQueue.shift();
                                    updates.queueChanged = true;
                                    queueDataIndexRef.current = 0;
                                }
                            }
                        }
                        
                        updates.newHrData[targetIndex] = hrValue;
                        const gapSize = isRespOrPleth ? 5 : 10;
                        const startGapIndex = (targetIndex + 1) % count;
                        const endGapIndex = (targetIndex + gapSize) % count;

                        if (startGapIndex < endGapIndex) {
                            updates.newHrData.fill(null, startGapIndex, endGapIndex + 1);
                        } else {
                            updates.newHrData.fill(null, startGapIndex, count);
                            updates.newHrData.fill(null, 0, endGapIndex + 1);
                        }
                        processedDataCount++;
                    }
                    
                    if (updates.newHrData.length > count) {
                        updates.newHrData = updates.newHrData.slice(updates.newHrData.length - count);
                    }
            
                    hrDataRef.current = updates.newHrData;
                    setHrData(updates.newHrData);
                    
                    // 🔥 큐 소비 — Recoil 대신 ref 직접 조작
                    if (updates.queueChanged) {
                        const consumedCount = updates.originalQueueLength - updates.newQueue.length;
                        if (consumedCount > 0) {
                            if (isPlayer) {
                                setHrDataQueue(prevQueue => prevQueue.slice(consumedCount));
                            }
                            sliceDataQueue(serialKey, type, consumedCount);
                        }
                    }

                    const isRespOrPleth = type === 'RESP' || type === 'PLETH';
                    const effectivePrevDataIndex = isRespOrPleth ? Math.floor(prevGlobalDataIndex / 2) : prevGlobalDataIndex;
                    const effectiveCurrentDataIndex = isRespOrPleth ? Math.floor(globalDataIndexRef.current / 2) : globalDataIndexRef.current;

                    const prevCycle = Math.floor(effectivePrevDataIndex / count);
                    const currentCycle = Math.floor(effectiveCurrentDataIndex / count);

                    const isGraphComplete = currentCycle > prevCycle && globalDataIndexRef.current > 0;

                    if (isGraphComplete) {
                        setChartIndex(globalDataIndexRef.current);
                        if (isPlayer) {
                            setHrDataQueue(prevQueue => {
                                if (prevQueue.length > bufferMaxCount) {
                                    return prevQueue.slice(prevQueue.length - bufferCount);
                                }
                                return prevQueue;
                            });
                        }
                        // 🔥 ref 기반 큐 트리밍
                        trimDataQueue(serialKey, type, bufferMaxCount, bufferCount);
                    }
                }

                performanceRef.current.lastFrameTime = currentTime;
                
            } catch (error) {
                console.error("❌ Rendering error:", error);
            } finally {
                isRenderingRef.current = false;
            }
            
            animationFrameId = requestAnimationFrame(animate);
        };
        
        performanceRef.current.lastFrameTime = performance.now();
        animationFrameId = requestAnimationFrame(animate);
        
        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [isDetecting, isInit, type, count, isPlayer, isPlaying]);

    // 🔥 bufferCount 알림 — ref 기반
    useEffect(() => {
        if (isPlayer) {
            handleBufferCount(hrDataQueue.length);
        } else {
            handleBufferCount(getDataQueueLength(serialKey, type));
        }
    }, [dataQueueLength, hrDataQueue.length, isPlayer])

    // 🔥 큐 길이 변경 감지용 (리렌더 최소화를 위해 인터벌 사용)
    useEffect(() => {
        if (isPlayer) return;
        const checkInterval = setInterval(() => {
            const len = getDataQueueLength(serialKey, type);
            if (len !== dataQueueLengthRef.current) {
                dataQueueLengthRef.current = len;
                setDataQueueLength(len);
            }
        }, 500);
        return () => clearInterval(checkInterval);
    }, [serialKey, type, isPlayer]);
    
    // 실시간 들어오는 데이터 큐에 추가
    useEffect(() => {
        if (data && data[type]) {
            let newData = data[type].map(item => {
                if (['LEADⅠ', 'LEADⅡ', 'LEADⅢ', 'CHEST', 'Pads/Paddle'].includes(type)) {
                    if (item === 'P') return 'parser';
                }
                return Number(item);
            });

            // 🔥 Recoil setDataQueue 대신 ref 기반
            if (isPlayer) {
                setHrDataQueue(prev => [...prev, newData]);
            } else {
                pushToDataQueue(serialKey, type, newData);
            }

            if (!isPlayer) {
                consumeWaveData(serialKey, type);
            } else {
                setPlayerBedData(prev => {
                    if (!prev.waveData?.[type]) return prev;
                    const { [type]: _, ...restBedData } = prev.waveData || {};
                    return { ...prev, waveData: restBedData };
                });
            }
        }
    }, [data, type]);
    
    return (
        <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg" className={props.className}>
            <path 
                d={points}
                fill="none"
                stroke={stroke}
                strokeWidth={strokeWidth}
            />
            {
                width && !isNaN(((maxData - ((maxData + minData) / 2)) / (maxData - minData))) &&
                <>
                    {
                        ['LEADⅠ', 'LEADⅡ', 'LEADⅢ', 'CHEST', 'Pads/Paddle', 'RR', 'RESP'].indexOf(type) !== -1 && viewType < 32 &&
                        <>
                            <rect
                                x={5}
                                y={(height / 2) - (pixelByMilliVoltage(minData, maxData, type, height) / 2)}
                                width={7}
                                height={pixelByMilliVoltage(minData, maxData, type, height)}
                                fill={`${parameterColors.waveParameterColors[type] ? `${parameterColors.waveParameterColors[type]}33` : (['LEADⅠ', 'LEADⅡ', 'LEADⅢ', 'CHEST', 'Pads/Paddle'].indexOf(type) !== -1 ? '#3dff3d33' : '#fde40133')}`}
                            />
                            <text
                                x={15}
                                y={(height / 2) + (pixelByMilliVoltage(minData, maxData, type, height) / 2) + 6} 
                                fill={`${parameterColors.waveParameterColors[type] || '#9F9F9F'}`}
                                fontSize="12"
                            >   
                                {['LEADⅠ', 'LEADⅡ', 'LEADⅢ', 'CHEST', 'Pads/Paddle'].indexOf(type) !== -1 ? '1mv' : '1Ω'}
                            </text>
                        </>
                    }
                </>
            }
            {
                width && viewType < 32 && isFirst &&
                <>
                    {(() => {
                        const dataPerSecond = (type === 'RESP' || type === 'PLETH') ? 125 : 250;
                        const baseCount = type === 'RESP' || type === 'PLETH' 
                            ? 675
                            : 1350;
                        const pixelPerSecond = (width / baseCount) * dataPerSecond;
                        
                        return (
                            <>
                                <path
                                    d={`M ${50} ${height - 20} L ${50} ${height - 13} L ${50 + pixelPerSecond} ${height - 13} L ${50 + pixelPerSecond} ${height - 20}`}
                                    fill="none"
                                    stroke={parameterColors.waveParameterColors[type] || '#9F9F9F'}
                                    strokeWidth={1}
                                />
                                <text
                                    x={55 + pixelPerSecond}
                                    y={height - 12}
                                    fill={parameterColors.waveParameterColors[type] || '#9F9F9F'}
                                    fontSize="12"
                                >
                                    25mm
                                </text>
                            </>
                        );
                    })()}
                </>
            }
            {
                isShowECGPacer &&
                hrData.map((item, idx) => {
                    if (item == 'parser') {
                        const x = ((idx - 1) / (hrData.length - 1)) * width; 
                        return (
                            <line
                                key={idx}
                                x1={x}
                                y1={yOffset + textYOffset}
                                x2={x}
                                y2={height}
                                stroke="yellow"
                                strokeWidth={1}
                            />
                        );
                    }
                    return null;
                })
            }
        </svg>
    );
}

export default Svg;